import { useState } from 'react';

export default function CoreServices() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const services = [
    {
      title: 'Medical Risk & Readiness Assessment',
      description: 'Strategic analysis of medical exposure across people, assets, itineraries, and environments. Applied to estates, aircraft, yachts, travel profiles, and high-profile operations.',
      image: 'https://public.readdy.ai/ai/img_res/728655bb5bd289a5c4094ccbc6ad3233.jpg'
    },
    {
      title: 'Emergency Response Architecture',
      description: 'Bespoke protocols for cardiac events, trauma, aviation and maritime incidents, evacuations, and VIP medical crises—integrated with existing security and operational teams.',
      image: 'https://readdy.ai/api/search-image?query=elite-emergency-medical-response-equipment-in-luxury-private-jet-sophisticated-medical-kit-high-end-aviation-interior-professional-emergency-preparedness-dark-elegant-setting&width=600&height=800&seq=service002&orientation=portrait'
    },
    {
      title: 'Embedded Medical Security',
      description: 'Discreet on-site medical professionals for aviation, maritime operations, events, estates, and executive activity where immediate response capability is essential.',
      image: 'https://readdy.ai/api/search-image?query=professional-medical-security-personnel-wearing-elegant-white-branded-polo-shirt-with-gold-caduceus-shield-logo-embroidered-on-left-chest-on-luxury-superyacht-deck-discreet-medical-professional-high-end-maritime-setting-sophisticated-atmosphere-clearly-visible-professional-uniform-branding&width=600&height=800&seq=servicelogo003&orientation=portrait'
    },
    {
      title: 'Staff & Crew Medical Training',
      description: 'Precision emergency readiness training for private aviation crews, yacht crews, estate staff, and security teams—focused on high-impact scenarios and real-world execution.',
      image: 'https://readdy.ai/api/search-image?query=elite-crew-medical-training-session-in-luxury-private-aviation-hangar-instructor-wearing-white-branded-polo-shirt-with-gold-caduceus-shield-logo-on-left-chest-teaching-crew-members-in-white-uniforms-with-matching-logo-professional-emergency-response-training-high-end-facility-sophisticated-medical-education-setting-clearly-visible-professional-uniform-branding&width=600&height=800&seq=servicelogo004&orientation=portrait'
    }
  ];

  return (
    <section className="bg-[#0D0D0D] py-16 sm:py-24 md:py-32 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-18 md:mb-24">
          <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.2em] text-[#606060] font-light mb-3 sm:mb-4">
            Core Capabilities
          </div>
          <h2 className="text-[28px] sm:text-[36px] md:text-[48px] font-bold text-[#F0F0F0] leading-tight font-serif">
            Core Medical Security Services
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className={`relative rounded-2xl overflow-hidden transition-all duration-400 cursor-pointer ${
                hoveredIndex === null || hoveredIndex === index ? 'opacity-100' : 'opacity-70'
              }`}
              style={{
                height: window.innerWidth < 640 ? '400px' : window.innerWidth < 1024 ? '500px' : '600px'
              }}
            >
              <div className="absolute inset-0">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
                <h3 className="text-base sm:text-lg md:text-xl lg:text-[22px] font-bold text-white tracking-wide mb-2 sm:mb-3">
                  {service.title}
                </h3>
                <p
                  className={`text-xs sm:text-sm md:text-[14px] text-[#B0B0B0] leading-relaxed transition-opacity duration-300 ${
                    hoveredIndex === index || window.innerWidth < 1024 ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 sm:mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-12">
          {[
            {
              title: 'Strategic Medical Readiness',
              description: 'Foundational medical security planning for principals and assets requiring assured preparedness without constant on-site presence.'
            },
            {
              title: 'Embedded Medical Security',
              description: 'Active medical protection for mobile, high-exposure, or operationally complex environments.'
            },
            {
              title: 'Principal Medical Security Command',
              description: 'Full-spectrum medical security oversight for UHNW principals and family offices operating globally.',
              note: 'By consultation only.'
            }
          ].map((tier, index) => (
            <div key={index} className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-2xl p-6 sm:p-8 md:p-10">
              <h3 className="text-lg sm:text-xl md:text-[24px] font-semibold text-[#F5F5F5] mb-3 sm:mb-4">
                {tier.title}
              </h3>
              <p className="text-sm sm:text-base md:text-[15px] text-[#A0A0A0] leading-relaxed mb-3 sm:mb-4">
                {tier.description}
              </p>
              {tier.note && (
                <p className="text-xs sm:text-sm md:text-[13px] text-[#707070] italic">
                  {tier.note}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
